export type WorkoutTypeKey = 'run' | 'cycle' | 'swim' | 'row' | 'walk' | 'other';
export type WorkoutFeeling = 'great' | 'good' | 'ok' | 'bad' | 'awful';
export type WorkoutModality = 'CARDIO' | 'STRENGTH';

/** One set of a strength exercise. */
export interface ExerciseSet {
  id?: string;
  setNo?: number;
  reps?: number | null;
  weightKg?: number | null;
  rpe?: number | null;
  completed?: boolean;
}

/** An exercise performed in a strength workout, with its sets. */
export interface WorkoutExercise {
  id?: string;
  position?: number;
  exerciseId?: string;
  exerciseName?: string;
  muscleGroup?: string | null;
  sets?: ExerciseSet[];
}

/** Catalog entry from GET /api/workouts/exercises. */
export interface Exercise {
  id: string;
  name: string;
  muscleGroup?: string | null;
  equipment?: string | null;
}

/** DTO с api-gateway. Поля не все приходят — большинство опциональные. */
export interface Workout {
  id: string;
  name?: string;
  /** CARDIO (default) or STRENGTH. */
  modality?: WorkoutModality;
  type?: WorkoutTypeKey;
  /** Бэкенд может отдавать имя типа: "Running"/"Cycling"/... */
  workoutTypeName?: string;
  /** Strength exercises (present when modality === 'STRENGTH'). */
  exercises?: WorkoutExercise[];
  startTime?: string;
  endTime?: string;
  /** В километрах. */
  distance?: number;
  /** В секундах. */
  duration?: number;
  averageHeartRate?: number;
  maxHeartRate?: number;
  caloriesBurned?: number;
  feeling?: WorkoutFeeling;
  location?: string;
  notes?: string;
  externalSource?: 'strava' | string;
  externalId?: string;
  /** Google-encoded polyline of the route (precision 5). */
  mapPolyline?: string | null;
  /** Per-segment splits computed from Strava streams. Absent for manual workouts. */
  splits?: WorkoutSplit[];
  /** Time-in-zone buckets (Z1–Z5) computed from the HR stream. Absent for manual workouts. */
  hrZones?: WorkoutHrZone[];
}

/** One per-segment split. `fastest` is the quickest full segment, not a personal record. */
export interface WorkoutSplit {
  index: number;
  distanceKm: number;
  durationSec: number;
  avgHeartRate?: number | null;
  elevationGainM?: number | null;
  fastest: boolean;
}

export interface WorkoutHrZone {
  zone: number;
  minBpm: number;
  maxBpm: number;
  seconds: number;
}

/** One page from GET /api/workouts. */
export interface WorkoutPage {
  content: Workout[];
  page: number;
  size: number;
  totalElements: number;
  hasNext: boolean;
}

/** Тело POST /api/workouts для кардио. */
export interface CreateWorkoutRequest {
  name: string;
  type: WorkoutTypeKey;
  date: string;
  durationMin: number;
  distanceKm: number;
  avgHeartRate: number;
  maxHeartRate: number;
  feeling: WorkoutFeeling;
}

export interface CreateStrengthSet {
  reps: number;
  weightKg: number;
  rpe?: number;
}
export interface CreateStrengthExercise {
  exerciseId?: string;
  exerciseName?: string;
  sets: CreateStrengthSet[];
}
/** Тело POST /api/workouts для силовой. */
export interface CreateStrengthWorkoutRequest {
  name: string;
  modality: 'STRENGTH';
  date: string;
  time?: string; // "HH:mm"
  durationMin?: number;
  feeling: WorkoutFeeling;
  exercises: CreateStrengthExercise[];
}

/** POST /api/workouts accepts either modality. */
export type CreateAnyWorkoutRequest = CreateWorkoutRequest | CreateStrengthWorkoutRequest;

export interface CardioGoals {
  sessions: number;
  distanceKm: number;
}

export const DEFAULT_GOALS: CardioGoals = { sessions: 12, distanceKm: 80 };

/** Per-type aggregation for the current month. Backend canonical names. */
export interface WorkoutStats {
  /** "Run" / "Ride" / "Swim" / "Rowing" / "Walk" / "Other" */
  workoutType: string;
  sessions: number;
  distanceKm: number;
  durationSeconds: number;
}
